import { isMultileg, type Market, type MarketAccount, type Security } from '@talos/kyoko';
import { resolveAggregationMarketSelectorValue } from './presets/resolveAggregationMarketSelectorValue';
import type { MarketSelectorItem, MarketSelectorPreset } from './types';

export type TradingSymbolPanelMarkets = {
  symbol: string;
  aggregation: string | undefined;
  marketAccounts: string[];
  markets: string[];
};

/** Maps combined selector names into layout-style `markets` vs `marketAccounts` (same as Market Data Card). */
export function splitMarketSelectorSelectionsToPanelFields(
  selections: string[],
  marketsByName: Map<string, Market>,
  marketAccountsByName: Map<string, MarketAccount>
): { markets: string[]; marketAccounts: string[] } {
  return {
    markets: selections.filter(name => marketsByName.has(name)),
    marketAccounts: selections.filter(name => marketAccountsByName.has(name)),
  };
}

/**
 * If the user's default aggregation applies to these items, returns panel fields; otherwise null.
 */
export function resolveDefaultAggregationToPanelFields(
  items: MarketSelectorItem[],
  useNewMarketSelector: boolean,
  defaultAggregationId: string | undefined,
  aggregationPresets: MarketSelectorPreset[] | undefined,
  marketsByName: Map<string, Market>,
  marketAccountsByName: Map<string, MarketAccount>
): { aggregation: string | undefined; markets: string[]; marketAccounts: string[] } | null {
  if (!useNewMarketSelector) {
    return null;
  }
  const resolved = resolveAggregationMarketSelectorValue({
    aggregationId: defaultAggregationId,
    aggregationPresets,
    items,
  });
  if (!resolved) {
    return null;
  }
  const { markets, marketAccounts } = splitMarketSelectorSelectionsToPanelFields(
    resolved.selections,
    marketsByName,
    marketAccountsByName
  );
  return {
    aggregation: resolved.type === 'preset' ? resolved.presetID : undefined,
    markets,
    marketAccounts,
  };
}

export function clearedTradingSymbolPanelMarkets(symbol: string): TradingSymbolPanelMarkets {
  return { symbol, aggregation: undefined, marketAccounts: [], markets: [] };
}

/** Inputs needed to derive default panel market fields for a trading symbol change. */
export type BuildTradingSymbolDefaultPanelMarketsParams = {
  nextSymbol: string;
  security: Security | undefined;
  useNewMarketSelector: boolean;
  defaultAggregationId: string | undefined;
  aggregationPresets: MarketSelectorPreset[] | undefined;
  marketsByName: Map<string, Market>;
  marketAccountsByName: Map<string, MarketAccount>;
  marketSelectorItems: MarketSelectorItem[];
};

/**
 * After a symbol change, applies the user's default aggregation when the new selector can resolve it.
 * Returns cleared panel fields when the symbol is unavailable, multileg, or has no applicable default.
 */
export function buildTradingSymbolDefaultPanelMarkets(
  params: BuildTradingSymbolDefaultPanelMarketsParams
): TradingSymbolPanelMarkets {
  const {
    nextSymbol,
    security,
    useNewMarketSelector,
    defaultAggregationId,
    aggregationPresets,
    marketsByName,
    marketAccountsByName,
    marketSelectorItems,
  } = params;

  const cleared = clearedTradingSymbolPanelMarkets(nextSymbol);
  if (!security || isMultileg(security)) {
    return cleared;
  }

  const fromDefault = resolveDefaultAggregationToPanelFields(
    marketSelectorItems,
    useNewMarketSelector,
    defaultAggregationId,
    aggregationPresets,
    marketsByName,
    marketAccountsByName
  );

  if (fromDefault) {
    return {
      symbol: nextSymbol,
      aggregation: fromDefault.aggregation,
      markets: fromDefault.markets,
      marketAccounts: fromDefault.marketAccounts,
    };
  }

  return cleared;
}
